/**
 * lib/teachingCycle.ts
 * ------------------------------------------------------------------
 * LKC's timetable runs a 6-DAY CYCLE (Day 1 … Day 6), not Monday–Friday
 * (TIMETABLE-PLAN.md). Which cycle day a date is decides which lessons run
 * that day — the live timetable, lesson registers, cover, the exam rota.
 *
 * THE RULE (the school, 31 Oct 2026): the cycle ROLLS ON through teaching
 * days and PAUSES on any day without teaching. A day has no teaching when it
 * is
 *   - a Saturday or Sunday;
 *   - a public holiday (HR's list, `publicHolidays`) — automatic;
 *   - outside every term (the school break, from Term Dates) — automatic;
 *   - a day the school closes during the year (sports day, a closure) —
 *     entered on School Settings → Teaching Days.
 *
 * The school fixes ONE date and the cycle day it is (the anchor); every other
 * date counts teaching days from it, forwards or back. Settings live at
 * `reportSchoolSettings/teachingCycle`. Pure: the screen and the timetable
 * pass the holidays and term check in.
 * ------------------------------------------------------------------ */

export interface CycleClosure {
  /** ISO date. */
  date: string;
  /** Why there is no teaching ("Sports day"). */
  reason: string;
}

export interface TeachingCycleSettings {
  /** Days in the cycle. 6 at LKC. */
  length: number;
  /** A teaching day whose cycle day is known — ISO date. '' until set. */
  anchorDate: string;
  /** Its cycle day, 1..length. */
  anchorDay: number;
  /** Days without teaching that are not holidays or breaks. */
  closures: CycleClosure[];
  updatedAt?: string;
  updatedBy?: string;
  updatedByName?: string;
}

export const TEACHING_CYCLE_DOC = 'teachingCycle';
export const DEFAULT_CYCLE: TeachingCycleSettings = { length: 6, anchorDate: '', anchorDay: 1, closures: [] };

/** What the rule needs from outside: holidays and the term calendar. */
export interface CycleContext {
  /** The holiday's name, or undefined. */
  holidayName: (iso: string) => string | undefined;
  /** Is this date inside a term? */
  inTerm: (iso: string) => boolean;
}

const DAY_MS = 86400000;
const toMs = (iso: string) => Date.UTC(Number(iso.slice(0, 4)), Number(iso.slice(5, 7)) - 1, Number(iso.slice(8, 10)));
const toIso = (ms: number) => new Date(ms).toISOString().slice(0, 10);
export const addDays = (iso: string, n: number) => toIso(toMs(iso) + n * DAY_MS);
export const weekdayOf = (iso: string) => new Date(toMs(iso)).getUTCDay();

/** Why a date has no teaching, or null when it is a teaching day. */
export function noTeachingReason(iso: string, settings: TeachingCycleSettings, ctx: CycleContext): string | null {
  const wd = weekdayOf(iso);
  if (wd === 0 || wd === 6) return 'Weekend';
  const holiday = ctx.holidayName(iso);
  if (holiday) return holiday;
  const closure = settings.closures.find((c) => c.date === iso);
  if (closure) return closure.reason || 'No teaching';
  if (!ctx.inTerm(iso)) return 'School break';
  return null;
}

/** The longest gap counted, either way: a year and a bit is plenty. */
const MAX_SPAN = 400;

/**
 * The cycle day of a date (1..length), or null when it is not a teaching day,
 * the anchor is not set, the anchor itself is not a teaching day, or the date
 * is more than a year from the anchor.
 */
export function cycleDayOf(iso: string, settings: TeachingCycleSettings, ctx: CycleContext): number | null {
  const { anchorDate, anchorDay, length } = settings;
  if (!anchorDate || !(length >= 1) || anchorDay < 1 || anchorDay > length) return null;
  if (noTeachingReason(iso, settings, ctx) || noTeachingReason(anchorDate, settings, ctx)) return null;
  const span = Math.round((toMs(iso) - toMs(anchorDate)) / DAY_MS);
  if (Math.abs(span) > MAX_SPAN) return null;
  const step = span >= 0 ? 1 : -1;
  let teaching = 0;
  for (let d = anchorDate; d !== iso;) {
    d = addDays(d, step);
    if (!noTeachingReason(d, settings, ctx)) teaching += step;
  }
  const zero = anchorDay - 1 + teaching;
  return ((zero % length) + length) % length + 1;
}

export interface CycleDate { date: string; day: number | null; reason: string | null }

/** Every date from `from` for `days` days: its cycle day, or why it has none. */
export function cycleCalendar(from: string, days: number, settings: TeachingCycleSettings, ctx: CycleContext): CycleDate[] {
  const out: CycleDate[] = [];
  for (let i = 0; i < days; i++) {
    const date = addDays(from, i);
    const reason = noTeachingReason(date, settings, ctx);
    out.push({ date, reason, day: reason ? null : cycleDayOf(date, settings, ctx) });
  }
  return out;
}

/** Today in Gaborone (UTC+2, no daylight saving), as an ISO date. */
export function gaboroneToday(now = Date.now()): string {
  return toIso(now + 2 * 3600 * 1000);
}
